'use client';

import { useEffect } from 'react';

const FOCUSABLE = 'input, textarea';
const FIELD_ROOTS = '.jade-input-field, .jade-textarea';
const NO_ZOOM_FONT_SIZE = 16;

/**
 * Sobe o campo tocado para 16px (evita o zoom automático do Safari em
 * campos < 16px) e aplica `transform: scale()` na razão inversa para o
 * texto continuar do tamanho original visualmente. Restaura tudo no blur.
 * Usa `touchstart`, não `focus`, como gatilho principal: no `focus` o zoom
 * já ocorreu. Um handler de `focus` cobre o caso de foco programático sem
 * toque (ex.: volta de um overlay do sistema — Apple Pay, Face ID,
 * desbloqueio de tela — que rouba e devolve o foco sem `touchstart`).
 *
 * `<select>` fica de fora: ele abre o picker nativo do iOS ao tocar, sem
 * dar zoom, e o `transform` nele encolhe a caixa inteira (moldura, seta),
 * não só o texto.
 */
export function usePreventIOSZoom(): void {
  useEffect(() => {
    if (!/iPad|iPhone|iPod/.test(navigator.userAgent)) return;

    let patched: HTMLElement | null = null;

    const restore = (element: HTMLElement | null) => {
      if (!element || element.dataset.originalFontSize === undefined) return;
      element.style.fontSize = element.dataset.originalFontSize;
      element.style.transform = element.dataset.originalTransform ?? '';
      delete element.dataset.originalFontSize;
      delete element.dataset.originalTransform;
    };

    const restoreStale = (current: HTMLElement) => {
      if (patched && patched !== current) {
        restore(patched);
        patched = null;
      }
    };

    const resolveControl = (node: EventTarget | null): HTMLElement | null => {
      if (!(node instanceof HTMLElement)) return null;
      if (node.matches(FOCUSABLE)) return node;
      return (
        node.closest(FIELD_ROOTS)?.querySelector<HTMLElement>(FOCUSABLE) ??
        null
      );
    };

    const applyFix = (control: HTMLElement | null) => {
      if (!control || control.dataset.originalFontSize !== undefined) return;

      const originalSize = parseFloat(getComputedStyle(control).fontSize);
      if (!originalSize || originalSize >= NO_ZOOM_FONT_SIZE) return;

      control.dataset.originalFontSize = control.style.fontSize;
      control.dataset.originalTransform = control.style.transform;
      control.style.fontSize = `${NO_ZOOM_FONT_SIZE}px`;
      control.style.transformOrigin = 'left center';
      control.style.transform = `scale(${originalSize / NO_ZOOM_FONT_SIZE})`;
      patched = control;
    };

    const handleTouchStart = (event: TouchEvent) => {
      const control = resolveControl(event.target);
      if (control) restoreStale(control);
      applyFix(control);
    };

    const handleFocus = (event: FocusEvent) => {
      const target = event.target;
      if (!(target instanceof HTMLElement) || !target.matches(FOCUSABLE)) {
        return;
      }
      restoreStale(target);
      applyFix(target);
    };

    const handleBlur = (event: FocusEvent) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      restore(target);
      if (patched === target) patched = null;
    };

    document.addEventListener('touchstart', handleTouchStart, true);
    document.addEventListener('focus', handleFocus, true);
    document.addEventListener('blur', handleBlur, true);

    return () => {
      restore(patched);
      document.removeEventListener('touchstart', handleTouchStart, true);
      document.removeEventListener('focus', handleFocus, true);
      document.removeEventListener('blur', handleBlur, true);
    };
  }, []);
}
